// Данные и разметка проводника: экраны, шаги визарда, стартовые карточки,
// сборка host-markup. Выделено из onboarding.ts (аудит, этап 2 — декомпозиция).
import {
  buildVideoScreenMarkup,
  getOnboardingRuntimeConfig,
  isAvatarScriptExperience,
  isAvatarLiveExperience,
  shouldShowVideoExplainer,
} from './onboarding-presenter';
import { buildAvatarPanelMarkup } from './onboarding-avatar-presenter';
import { buildLiveDialogPanelMarkup } from './onboarding-live-presenter';
import type { OnboardingAnswers } from '../shared/onboarding-gateway';

export type OnboardingScreen =
  | 'video'
  | 'welcome'
  | 'example'
  | 'wizard'
  | 'summary'
  | 'scales'
  | 'pause';

export type WizardStep = {
  key: keyof OnboardingAnswers;
  context: string;
  title: string;
  lead: string;
  placeholder: string;
  helpHtml: string;
};

export const START_CARDS = [
  {
    title: 'Одна ситуация за 10–15 минут',
    text: 'Разберите небольшой бытовой или рабочий эпизод — без методологии и без заполнения всей карты.',
  },
  {
    title: 'Четыре простых вопроса',
    text: 'Факт → реакция → ваша версия → проверяемый шаг. Это не тест и не оценка.',
  },
  {
    title: 'Сначала пример, не эталон',
    text: 'Синтетическая запись показывает структуру. Ваш ответ может быть совсем другим.',
  },
  {
    title: 'Без баллов и похвалы',
    text: 'Инструмент самонаблюдения, не диагноз. Можно остановиться в любой момент.',
  },
  {
    title: 'Полный кабинет всегда рядом',
    text: 'Карта, сферы и две шкалы остаются доступны — проводник не блокирует опытный режим.',
  },
] as const;

export const WIZARD_STEPS: WizardStep[] = [
  {
    key: 'fact',
    context: 'Сейчас: отделяем наблюдаемое от объяснения',
    title: 'Что произошло?',
    lead: 'Опишите один конкретный эпизод так, чтобы другой человек мог отличить событие от вашей версии причин.',
    placeholder: 'Где и когда это было? Что именно сделали или сказали участники?',
    helpHtml:
      '<p><strong>Пример:</strong> «В 15:00 открыл письмо. За 20 минут трижды переключился на новости и не отправил его».</p>' +
      '<p>Избегайте слов «специально», «потому что», «всегда» — они уже объясняют событие.</p>',
  },
  {
    key: 'reaction',
    context: 'Сейчас: замечаем реакцию без оценки',
    title: 'Что произошло внутри вас?',
    lead: 'Отдельно запишите телесное ощущение, автоматическую мысль и импульс к действию.',
    placeholder: 'Например: напряжение в плечах; мысль…; хотелось…',
    helpHtml:
      '<p><strong>Пример:</strong> «Напряжение в плечах, мысль “сначала надо всё идеально сформулировать”, импульс открыть ещё один источник».</p>' +
      '<p>Здесь нет правильной эмоции. Не нужно подбирать методологический термин.</p>',
  },
  {
    key: 'hypothesis',
    context: 'Сейчас: формулируем версию, а не истину',
    title: 'Как вы объяснили себе эту реакцию?',
    lead: 'Начните со слов «возможно» или «моя версия». Это помогает не превращать объяснение в факт.',
    placeholder: 'Возможно, я… Моя версия состоит в том, что…',
    helpHtml:
      '<p><strong>Пример:</strong> «Возможно, я избегал риска получить несогласие и называл это подготовкой».</p>' +
      '<p>Эта версия может измениться. AI тоже предлагает только гипотезу для проверки.</p>',
  },
  {
    key: 'action',
    context: 'Сейчас: выбираем проверяемое действие',
    title: 'Какой небольшой шаг можно проверить?',
    lead: 'Выберите одно безопасное и обратимое действие. Оно не должно доказывать «проработанность».',
    placeholder: 'В следующий похожий момент я попробую…',
    helpHtml:
      '<p><strong>Пример:</strong> «Написать черновик из трёх предложений за пять минут и перечитать его только один раз».</p>' +
      '<p>Хороший шаг конкретен: понятно, когда он сделан и что можно наблюдать после.</p>',
  },
];

export const SUMMARY_LABELS: Record<keyof OnboardingAnswers, string> = {
  fact: 'Наблюдаемый факт',
  reaction: 'Внутренняя реакция',
  hypothesis: 'Моё объяснение-гипотеза',
  action: 'Проверяемый шаг',
};

export function escapeHtml(value: string): string {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export function buildHostMarkup(): string {
  const videoMarkup =
    shouldShowVideoExplainer() && getOnboardingRuntimeConfig().video
      ? buildVideoScreenMarkup(getOnboardingRuntimeConfig().video!)
      : '';

  const avatarMarkup = isAvatarLiveExperience()
    ? buildLiveDialogPanelMarkup()
    : isAvatarScriptExperience()
      ? buildAvatarPanelMarkup()
      : '';
  const layoutClass = avatarMarkup ? 'onboarding-layout-with-avatar' : '';

  const cardsHtml = START_CARDS.map(
    (card) => `
      <article class="onboarding-card">
        <strong>${escapeHtml(card.title)}</strong>
        <span>${escapeHtml(card.text)}</span>
      </article>
    `
  ).join('');

  const dotsHtml = WIZARD_STEPS.map(() => '<span class="onboarding-progress-dot" aria-hidden="true"></span>').join('');

  return `
    <div class="onboarding-layout ${layoutClass}">
    ${avatarMarkup}
    <div class="onboarding-main">
    <div class="onboarding-global-actions" id="onboarding-global-actions" hidden>
      <button type="button" class="onboarding-btn-link" data-onboarding-action="pause">Остановиться</button>
      <button type="button" class="onboarding-btn-link" data-onboarding-action="delete-draft">Удалить черновик</button>
    </div>

    ${videoMarkup}

    <section class="onboarding-screen" data-onboarding-screen="welcome" ${videoMarkup ? 'hidden' : ''} aria-label="Первые шаги">
      <p class="onboarding-eyebrow">Первые шаги</p>
      <h2>Разберите одну небольшую ситуацию без оценки себя</h2>
      <p class="onboarding-lead">Обычно это занимает 10–15 минут. Не нужно знать методологию или заполнять всю карту.</p>
      <div class="onboarding-cards" role="list">${cardsHtml}</div>
      <div class="onboarding-contract" role="note">
        <p><strong>Это инструмент самонаблюдения, не диагноз и не оценка личности.</strong></p>
        <p>Возьмите нейтральную бытовую или рабочую ситуацию. Не используйте острое травматическое событие. Можно остановиться в любой момент.</p>
        <p class="onboarding-privacy-note">При пропуске проводника в браузере сохраняется отметка «проводник просмотрен» — без текста ваших записей.</p>
      </div>
      <div class="onboarding-actions onboarding-actions-end">
        <div class="onboarding-action-group">
          <button type="button" class="onboarding-btn onboarding-btn-secondary" data-onboarding-action="skip-guide">Сразу открыть полный кабинет</button>
          <button type="button" class="onboarding-btn onboarding-btn-primary" data-onboarding-action="begin">Начать</button>
        </div>
      </div>
    </section>

    <section class="onboarding-screen" data-onboarding-screen="example" hidden aria-label="Пример записи">
      <p class="onboarding-eyebrow">Один возможный пример, не эталон</p>
      <h2>Как отделить наблюдение от объяснения</h2>
      <p class="onboarding-lead">Здесь нет «правильного» ответа. Важна граница между тем, что произошло, и версией о причинах.</p>
      <dl class="onboarding-example-card">
        <div class="onboarding-example-label">Вымышленная ситуация: отложенное рабочее письмо</div>
        <div class="onboarding-example-row">
          <dt>Факт</dt>
          <dd>В 15:00 открыл черновик письма. За следующие 20 минут трижды переключился на новости и не отправил письмо.</dd>
        </div>
        <div class="onboarding-example-row">
          <dt>Реакция</dt>
          <dd>Напряжение в плечах, мысль «сначала надо всё идеально сформулировать», импульс открыть ещё один источник.</dd>
        </div>
        <div class="onboarding-example-row">
          <dt>Моё объяснение</dt>
          <dd>Возможно, я избегал риска получить несогласие и называл это подготовкой.</dd>
        </div>
        <div class="onboarding-example-row">
          <dt>Проверяемый шаг</dt>
          <dd>Написать черновик из трёх предложений за пять минут и перечитать его только один раз.</dd>
        </div>
      </dl>
      <details class="onboarding-help">
        <summary>Почему пример устроен так?</summary>
        <div class="onboarding-help-body">
          <p>Факт можно проверить по наблюдаемым действиям. Реакция описывает тело, мысли и импульс. Объяснение помечено как версия. Шаг маленький, безопасный и допускает проверку.</p>
        </div>
      </details>
      <div class="onboarding-actions">
        <button type="button" class="onboarding-btn-link" data-onboarding-action="back-welcome">Назад</button>
        <button type="button" class="onboarding-btn onboarding-btn-primary" data-onboarding-action="start-wizard">Разобрать свою ситуацию</button>
      </div>
    </section>

    <section class="onboarding-screen" data-onboarding-screen="wizard" hidden aria-label="Визард записи">
      <div class="onboarding-progress" aria-live="polite">
        <span id="onboarding-progress-label">Шаг 1 из 4</span>
        <div class="onboarding-progress-dots">${dotsHtml}</div>
      </div>
      <p class="onboarding-eyebrow" id="onboarding-wizard-context">Сейчас: фиксируем наблюдаемое</p>
      <h2 id="onboarding-wizard-title">Что произошло?</h2>
      <p class="onboarding-lead" id="onboarding-wizard-lead"></p>
      <label class="onboarding-field-label" for="onboarding-wizard-answer">Ваша запись</label>
      <textarea id="onboarding-wizard-answer" class="onboarding-textarea" maxlength="1800" aria-describedby="onboarding-wizard-note onboarding-wizard-error"></textarea>
      <p class="onboarding-field-note" id="onboarding-wizard-note">Можно написать 1–3 коротких предложения.</p>
      <div class="onboarding-field-error" id="onboarding-wizard-error" role="alert"></div>
      <details class="onboarding-help" id="onboarding-wizard-help">
        <summary>Показать пример и пояснение</summary>
        <div class="onboarding-help-body" id="onboarding-wizard-help-body"></div>
      </details>
      <div class="onboarding-actions">
        <button type="button" class="onboarding-btn-link" data-onboarding-action="wizard-back">Назад</button>
        <div class="onboarding-action-group">
          <button type="button" class="onboarding-btn onboarding-btn-secondary" data-onboarding-action="wizard-skip">Пропустить</button>
          <button type="button" class="onboarding-btn onboarding-btn-primary" data-onboarding-action="wizard-next">Продолжить</button>
        </div>
      </div>
    </section>

    <section class="onboarding-screen" data-onboarding-screen="summary" hidden aria-label="Сводка записи">
      <p class="onboarding-eyebrow">Первая фиксация</p>
      <h2>Запись собрана</h2>
      <p class="onboarding-neutral-note">Ниже — не оценка и не вывод о вас. Это четыре разных вида материала, к которым можно вернуться позже.</p>
      <div class="onboarding-summary-card" id="onboarding-summary-content"></div>
      <div class="onboarding-actions">
        <button type="button" class="onboarding-btn onboarding-btn-secondary" data-onboarding-action="edit-wizard">Изменить запись</button>
        <div class="onboarding-action-group">
          <button type="button" class="onboarding-btn onboarding-btn-secondary" data-onboarding-action="show-scales">Как это связано с картой?</button>
          <button type="button" class="onboarding-btn onboarding-btn-primary" data-onboarding-action="save-draft">Сохранить как черновик</button>
        </div>
      </div>
    </section>

    <section class="onboarding-screen" data-onboarding-screen="scales" hidden aria-label="Два масштаба наблюдения">
      <p class="onboarding-eyebrow">Два масштаба наблюдения</p>
      <h2>Это не лестница из 14 обязательных этапов</h2>
      <p class="onboarding-lead">Одни вопросы помогают разобрать конкретное событие. Другие нужны лишь время от времени, чтобы увидеть длительную динамику повторяющегося механизма.</p>
      <div class="onboarding-scale-grid">
        <article class="onboarding-scale-card">
          <h3>Разбор события</h3>
          <p>Короткий масштаб: что случилось сегодня и какое действие можно проверить.</p>
          <ol class="onboarding-scale-list">
            <li>Событие</li>
            <li>Реакция</li>
            <li>Моё объяснение</li>
            <li>Механизм</li>
            <li>Моё участие</li>
            <li>Новый шаг</li>
            <li>Проверка</li>
          </ol>
        </article>
        <article class="onboarding-scale-card">
          <h3>Длительная динамика</h3>
          <p>Редкая сверка: как один механизм проявляется и меняется в повторных событиях.</p>
          <ul class="onboarding-scale-list">
            <li>Не заполняется за один раз</li>
            <li>Не подтверждает «уровень» человека</li>
            <li>Не определяется решением AI</li>
            <li>Может обновляться и пересматриваться</li>
          </ul>
        </article>
      </div>
      <div class="onboarding-contract" role="note">
        <p><strong>Почему нет прогресс-бара?</strong></p>
        <p>Количество заполненных полей не показывает качество наблюдения. Повторное событие даёт новый материал, а не очки и не переход на уровень.</p>
      </div>
      <div class="onboarding-actions">
        <button type="button" class="onboarding-btn onboarding-btn-secondary" data-onboarding-action="back-summary">Вернуться к записи</button>
        <button type="button" class="onboarding-btn onboarding-btn-primary" data-onboarding-action="open-cabinet">Открыть полный кабинет</button>
      </div>
    </section>

    <section class="onboarding-screen" data-onboarding-screen="pause" hidden aria-label="Пауза">
      <p class="onboarding-eyebrow">Пауза</p>
      <h2>Можно остановиться здесь</h2>
      <div class="onboarding-contract">
        <p>Черновик сохранён в этой сессии браузера. Вы можете вернуться к нему позже или удалить.</p>
        <p>Если разбор усиливает панику, бессонницу или навязчивую самокритику, лучше прекратить и обратиться за подходящей профессиональной поддержкой.</p>
      </div>
      <div class="onboarding-actions">
        <button type="button" class="onboarding-btn onboarding-btn-secondary" data-onboarding-action="delete-draft">Удалить черновик</button>
        <button type="button" class="onboarding-btn onboarding-btn-primary" data-onboarding-action="resume">Вернуться</button>
      </div>
    </section>
    </div>
    </div>
  `;
}
